import React, { useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as LinkIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { IProcessManager } from '../../Interfaces/IProcessManager';

interface IProps {
  /** Nur PMs mit idProcessManagerType=22 reinreichen. */
  processManagerArray: IProcessManager[];
}

/** Strukturierte Sicht auf das ``data``-JSON eines HBCI-Trackers. Alle
 *  Felder optional, da sie der Backend-Driver erst beim ersten Sweep
 *  setzt. */
interface IHbciTrackerData {
  idHbciAccount?: number | string;
  HbciIBAN?: string;
  IBAN?: string;
  HbciBankName?: string;
  BankName?: string;
  HbciBIC?: string;
  BIC?: string;
  TargetAmount?: string | number;
  LastBalance?: string | number;
  LastCheckedAt?: string;
  TargetReachedAt?: string;
  Error?: string;
}

const formatBalance = (val: number | string | null | undefined): string => {
  if (val === null || val === undefined || val === '') return '–';
  const num =
    typeof val === 'number' ? val : parseFloat(String(val).replace(',', '.'));
  if (!Number.isFinite(num)) return '–';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(num);
};

const formatDateTime = (val?: string | null): string => {
  if (!val) return '–';
  try {
    return new Date(val).toLocaleString('de-DE');
  } catch {
    return val;
  }
};

const parseNumber = (raw: unknown): number | null => {
  if (raw === null || raw === undefined || raw === '') return null;
  const n =
    typeof raw === 'number' ? raw : parseFloat(String(raw).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const getStateDot = (pm: IProcessManager, hasError: boolean) => {
  if (pm.idProcessManagerState !== 1) {
    return { color: '#eeeeee', title: 'Nachverfolgung abgeschlossen' };
  }
  if (hasError) {
    return { color: '#f44336', title: 'Fehler beim letzten Abruf' };
  }
  if (!pm.LastTry_at) {
    return { color: '#9e9e9e', title: 'Noch nicht geprüft' };
  }
  const ageDays = Math.floor(
    (Date.now() - new Date(String(pm.LastTry_at)).getTime()) / 86400000
  );
  if (ageDays > 2)
    return { color: '#ffeb3b', title: `Letzte Prüfung vor ${ageDays} Tagen` };
  return { color: '#4caf50', title: 'Aktiv und aktuell' };
};

/**
 * Übersicht aller Notaranderkonto-Tracker (PMType=22) — analog zu
 * ProcessManagerCommercialTrackingTable. Read-only, klickbar zum
 * Vorgang. Gruppiert pro Bank in einem Accordion.
 *
 * Die eigentliche Bearbeitung (Refresh, Buchungen, Automationen)
 * findet im Vorgang selbst statt.
 */
export const ProcessManagerHbciTrackingTable: React.FC<IProps> = ({
  processManagerArray,
}) => {
  const navigate = useNavigate();

  // Pro Bank gruppieren (Schlüssel = BankName fallback BIC fallback "Unbekannte Bank")
  const groups = useMemo(() => {
    const g = new Map<string, IProcessManager[]>();
    processManagerArray.forEach(pm => {
      const data = (pm.data as IHbciTrackerData) || {};
      const key =
        data.HbciBankName ||
        data.BankName ||
        data.HbciBIC ||
        data.BIC ||
        'Unbekannte Bank';
      const arr = g.get(key) ?? [];
      arr.push(pm);
      g.set(key, arr);
    });
    return g;
  }, [processManagerArray]);

  if (processManagerArray.length === 0) return null;

  const renderTable = (pms: IProcessManager[]) => (
    <Table size='small'>
      <TableHead>
        <TableRow>
          <TableCell>Status</TableCell>
          <TableCell>Erstellt am</TableCell>
          <TableCell>IBAN</TableCell>
          <TableCell align='right'>Letzter Saldo</TableCell>
          <TableCell align='right'>Zielsumme</TableCell>
          <TableCell>Letzte Prüfung</TableCell>
          <TableCell>Prüfungen</TableCell>
          <TableCell>Aktion</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {pms.map(pm => {
          const data = (pm.data as IHbciTrackerData) || {};
          const target = parseNumber(data.TargetAmount);
          const lastBalance = parseNumber(data.LastBalance);
          const targetReached = Boolean(data.TargetReachedAt);
          const hasError = Boolean(data.Error);
          const state = getStateDot(pm, hasError);
          const iban =
            data.HbciIBAN || data.IBAN || `#${data.idHbciAccount ?? '?'}`;

          return (
            <TableRow key={`pm-hbci-${pm.idProcessManager}`}>
              <TableCell>
                <Tooltip
                  title={
                    hasError
                      ? data.Error || state.title
                      : targetReached
                        ? `Zielsumme erreicht am ${formatDateTime(data.TargetReachedAt)}`
                        : state.title
                  }
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: state.color,
                      border: '1px solid rgba(0,0,0,0.1)',
                      display: 'inline-block',
                    }}
                  />
                </Tooltip>
                {targetReached && !hasError && (
                  <Chip
                    size='small'
                    color='success'
                    label='Zielsumme erreicht'
                    sx={{ ml: 1 }}
                  />
                )}
              </TableCell>
              <TableCell>
                {pm.Created_at ? new Date(pm.Created_at).toLocaleString() : '–'}
              </TableCell>
              <TableCell>
                <Typography variant='body2' fontFamily='monospace'>
                  {iban}
                </Typography>
              </TableCell>
              <TableCell align='right'>{formatBalance(lastBalance)}</TableCell>
              <TableCell align='right'>
                {target !== null ? formatBalance(target) : '–'}
              </TableCell>
              <TableCell>
                <Typography variant='caption' color='text.secondary'>
                  {formatDateTime(
                    data.LastCheckedAt ||
                      (pm.LastTry_at ? String(pm.LastTry_at) : null)
                  )}
                </Typography>
              </TableCell>
              <TableCell>{pm.Tries ?? 0}</TableCell>
              <TableCell>
                {pm.idLegalTransaction ? (
                  <Tooltip title='Vorgang öffnen'>
                    <IconButton
                      onClick={() =>
                        navigate(`/order_book/${pm.idLegalTransaction}`)
                      }
                    >
                      <LinkIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  '–'
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  return (
    <>
      <Typography variant='h5' sx={{ mt: 3 }}>
        Notaranderkonten
      </Typography>

      {Array.from(groups.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([bank, pms]) => (
          <Accordion key={`pm-hbci-bank-${bank}`} defaultExpanded>
            <AccordionSummary>
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                sx={{ width: '100%' }}
              >
                <Typography>{bank}</Typography>
                <Chip size='small' label={pms.length} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>{renderTable(pms)}</AccordionDetails>
          </Accordion>
        ))}
    </>
  );
};
